import _extends from '@babel/runtime/helpers/esm/extends';
import React, { useState, useMemo } from 'react';
import { MaskType, MaskedTextInput } from '@paypalcorp/pp-react-masked-text-input';
import { Tooltip } from '@paypalcorp/pp-react-tooltip';
import { Row, Col } from '@paypalcorp/pp-react-grid';
import { CreditCardIcon } from '@paypalcorp/pp-react-icons';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { css, useTheme, jsx, ClassNames } from '@emotion/react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { devWarning } from '@paypalcorp/pp-react-utils';
var dateMaskOptions = {
  mask: 'm/y',
  blocks: {
    y: {
      mask: MaskType.Range,
      from: 1,
      to: 99,
      maxLength: 2
    },
    m: {
      mask: MaskType.Range,
      from: 1,
      to: 99,
      maxLength: 2
    }
  },
  lazy: true
};
/**
 * Card Hook
 */

function useCreditCardInput(_ref) {
  var _ref$initialCardNumbe = _ref.initialCardNumberDetails,
    initialCardNumberDetails = _ref$initialCardNumbe === void 0 ? {} : _ref$initialCardNumbe,
    _ref$initialCvvDetail = _ref.initialCvvDetails,
    initialCvvDetails = _ref$initialCvvDetail === void 0 ? {} : _ref$initialCvvDetail,
    _ref$initialExpiratio = _ref.initialExpirationDetails,
    initialExpirationDetails = _ref$initialExpiratio === void 0 ? {} : _ref$initialExpiratio,
    onCardNumberChange = _ref.onCardNumberChange,
    onCvvChange = _ref.onCvvChange,
    onExpirationChange = _ref.onExpirationChange;
  var _useState = useState(initialCvvDetails.value),
    _useState2 = _slicedToArray(_useState, 2),
    cvvNumber = _useState2[0],
    setCvvNumber = _useState2[1];
  var _useState3 = useState(initialCvvDetails.errorText),
    _useState4 = _slicedToArray(_useState3, 2),
    cvvNumberError = _useState4[0],
    _setCvvNumberError = _useState4[1];
  var _useState5 = useState(initialCvvDetails.helperText),
    _useState6 = _slicedToArray(_useState5, 2),
    cvvNumberHelper = _useState6[0],
    _setCvvNumberHelper = _useState6[1];
  var _useState7 = useState(initialExpirationDetails.value),
    _useState8 = _slicedToArray(_useState7, 2),
    cardDate = _useState8[0],
    setCardDate = _useState8[1];
  var _useState9 = useState(initialExpirationDetails.errorText),
    _useState10 = _slicedToArray(_useState9, 2),
    cardDateError = _useState10[0],
    _setCardDateError = _useState10[1];
  var _useState11 = useState(initialExpirationDetails.helperText),
    _useState12 = _slicedToArray(_useState11, 2),
    cardDateHelper = _useState12[0],
    _setCardDateHelper = _useState12[1];
  var _useState13 = useState(initialCardNumberDetails.value),
    _useState14 = _slicedToArray(_useState13, 2),
    cardNumber = _useState14[0],
    setCardNumber = _useState14[1];
  var _useState15 = useState(initialCardNumberDetails.errorText),
    _useState16 = _slicedToArray(_useState15, 2),
    cardNumberError = _useState16[0],
    _setCardNumberError = _useState16[1];
  var _useState17 = useState(initialCardNumberDetails.helperText),
    _useState18 = _slicedToArray(_useState17, 2),
    cardNumberHelper = _useState18[0],
    _setCardNumberHelper = _useState18[1];
  var _useState19 = useState(initialCardNumberDetails.rightIcon),
    _useState20 = _slicedToArray(_useState19, 2),
    cardIcon = _useState20[0],
    _setCardIcon = _useState20[1];
  var _useState21 = useState(initialCvvDetails.tooltipOptions ? initialCvvDetails.tooltipOptions.imageElement : null),
    _useState22 = _slicedToArray(_useState21, 2),
    tooltipIcon = _useState22[0],
    _setTooltipIcon = _useState22[1];
  var _useState23 = useState(initialCvvDetails.tooltipOptions ? initialCvvDetails.tooltipOptions.description : null),
    _useState24 = _slicedToArray(_useState23, 2),
    tooltipText = _useState24[0],
    _setTooltipText = _useState24[1];
  var _useState25 = useState(initialCardNumberDetails.maskValue),
    _useState26 = _slicedToArray(_useState25, 2),
    cardNumberMask = _useState26[0],
    _setCardNumberMask = _useState26[1];
  var _useState27 = useState(initialCvvDetails.maskValue),
    _useState28 = _slicedToArray(_useState27, 2),
    cvvNumberMask = _useState28[0],
    _setCvvNumberMask = _useState28[1];
  var handleChange = function (val, unmaskedVal, typedValue, field) {
    /* istanbul ignore next */
    switch (field) {
      case 'CARD_NUMBER':
        setCardNumber(val);
        if (onCardNumberChange) onCardNumberChange(val, unmaskedVal, typedValue);
        break;
      case 'CARD_CVV':
        setCvvNumber(val);
        if (onCvvChange) onCvvChange(val, unmaskedVal, typedValue);
        break;
      case 'CARD_EXPIRATION':
        setCardDate(val);
        if (onExpirationChange) onExpirationChange(val, unmaskedVal, typedValue);
        break;
    }
  };
  var cardNumberDetails = {
    name: initialCardNumberDetails.name || 'card_number',
    label: initialCardNumberDetails.label || 'Card Number',
    value: cardNumber,
    maskOptions: {
      mask: cardNumberMask || '0000 0000 0000 0000',
      lazy: true
    },
    rightIcon: cardIcon,
    errorText: cardNumberError,
    helperText: cardNumberHelper,
    onAccept: function onAccept(val, unmaskedVal, typedValue) {
      return handleChange(val, unmaskedVal, typedValue, 'CARD_NUMBER');
    }
  };
  var cvvDetails = {
    name: initialCvvDetails.name || 'cvv_number',
    label: initialCvvDetails.label || 'CVV',
    value: cvvNumber,
    maskOptions: {
      mask: cvvNumberMask || '000',
      lazy: true
    },
    errorText: cvvNumberError,
    helperText: cvvNumberHelper,
    tooltipOptions: {
      name: initialCvvDetails.tooltipOptions && initialCvvDetails.tooltipOptions.name ? initialCvvDetails.tooltipOptions.name : 'credit_input-tooltip',
      description: tooltipText,
      imageElement: tooltipIcon
    },
    onAccept: function onAccept(val, unmaskedVal, typedValue) {
      return handleChange(val, unmaskedVal, typedValue, 'CARD_CVV');
    }
  };
  var expirationDetails = {
    name: initialExpirationDetails.name || 'card_expiration',
    label: initialExpirationDetails.label || 'Expiration',
    value: cardDate,
    errorText: cardDateError,
    helperText: cardDateHelper,
    maskOptions: dateMaskOptions,
    onAccept: function onAccept(val, unmaskedVal, typedValue) {
      return handleChange(val, unmaskedVal, typedValue, 'CARD_EXPIRATION');
    }
  };
  return {
    setCardNumberMask: function setCardNumberMask(value) {
      _setCardNumberMask(value);
    },
    setCvvNumberMask: function setCvvNumberMask(value) {
      _setCvvNumberMask(value);
    },
    setTooltipIcon: function setTooltipIcon(icon) {
      _setTooltipIcon(icon);
    },
    setCardIcon: function setCardIcon(icon) {
      _setCardIcon(icon);
    },
    setTooltipText: function setTooltipText(value) {
      _setTooltipText(value);
    },
    setCardNumberError: function setCardNumberError(value) {
      _setCardNumberError(value);
    },
    setCvvNumberError: function setCvvNumberError(value) {
      _setCvvNumberError(value);
    },
    setCardDateError: function setCardDateError(value) {
      _setCardDateError(value);
    },
    setCvvNumberHelper: function setCvvNumberHelper(value) {
      _setCvvNumberHelper(value);
    },
    setCardDateHelper: function setCardDateHelper(value) {
      _setCardDateHelper(value);
    },
    setCardNumberHelper: function setCardNumberHelper(value) {
      _setCardNumberHelper(value);
    },
    cardNumberDetails: cardNumberDetails,
    cvvDetails: cvvDetails,
    expirationDetails: expirationDetails
  };
}
var CreditCardInputUtils = {
  useCreditCardInput: useCreditCardInput
};
var _templateObject, _templateObject2;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }
  var sysSizeIconMd = tokens.sysSizeIconMd;
  var cvvImg = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: cvv_image;\n    height: ", ";\n  "])), sysSizeIconMd);
  var numberInputRTL = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: number_input_rtl;\n    [dir='rtl'] & {\n      direction: ltr;\n      text-align: right;\n    }\n  "])));
  return {
    cvvImg: cvvImg,
    numberInputRTL: numberInputRTL
  };
}
var CreditCardInput = function (_ref) {
  var cardNumberDetails = _ref.cardNumberDetails,
    expirationDetails = _ref.expirationDetails,
    cvvDetails = _ref.cvvDetails,
    _ref$cdnDomain = _ref.cdnDomain,
    cdnDomain = _ref$cdnDomain === void 0 ? 'https://www.paypalobjects.com' : _ref$cdnDomain;
  // If theme is not directly provided, get from themeContext
  var tokens = useTheme();
  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);
  var tooltipOptions = cvvDetails.tooltipOptions;
  /* istanbul ignore next */

  if (cvvDetails.tooltipOptions) delete cvvDetails.tooltipOptions; // Add default tooltipOptions if tooltipOptions not passed

  /* istanbul ignore next */

  if (!tooltipOptions) {
    tooltipOptions = {
      name: 'credit_input-tooltip',
      description: 'The CVV is the 3-digit number on the back of your card',
      imageElement: jsx("img", {
        src: "".concat(cdnDomain, "/paypal-ui/components/csc-card/csc-card-back.svg"),
        alt: "cvv",
        css: _styles.cvvImg,
        "data-ppui": "true"
      })
    };
  } // Add default tooltip desciption if tooltip description is not present

  if (tooltipOptions && !tooltipOptions.description) tooltipOptions.description = 'The CVV is the 3-digit number on the back of your card'; // Add default tooltip image if tooltip image is not present

  if (tooltipOptions && !tooltipOptions.imageElement) {
    tooltipOptions.imageElement = jsx("img", {
      src: "".concat(cdnDomain, "/paypal-ui/components/csc-card/csc-card-back.svg"),
      alt: "cvv",
      css: _styles.cvvImg,
      "data-ppui": "true"
    });
  } // Fallback to `CreditCardIcon` if no cardIcon passed after 5 digits are entered

  if (cardNumberDetails.value && cardNumberDetails.value.length > 4 && !cardNumberDetails.rightIcon) {
    cardNumberDetails.rightIcon = jsx(CreditCardIcon, {
      "data-ppui": "true"
    });
  }
  var cardNumberField = jsx(ClassNames, {
    "data-ppui": "true"
  }, function (_ref2) {
    var css = _ref2.css;
    return jsx(MaskedTextInput, _extends({
      inputMode: "numeric"
    }, cardNumberDetails, {
      inputClassName: css(_styles.numberInputRTL),
      "data-ppui": "true"
    }));
  });
  return jsx(React.Fragment, null, jsx(Row, {
    "data-ppui-info": "credit-card-input_2.1.7"
  }, jsx(Col, {
    form: "full",
    "data-ppui": "true"
  }, cardNumberField)), jsx(Row, {
    "data-ppui-info": "credit-card-input_2.1.7"
  }, jsx(Col, {
    form: "half",
    xs: true,
    "data-ppui": "true"
  }, jsx(MaskedTextInput, _extends({
    inputMode: "numeric"
  }, expirationDetails, {
    "data-ppui": "true"
  }))), jsx(Col, {
    form: "half",
    xs: true,
    "data-ppui": "true"
  }, jsx(MaskedTextInput, _extends({
    inputMode: "numeric",
    rightIcon: jsx(Tooltip, _extends({
      imagePosition: "top-left"
    }, tooltipOptions, {
      "data-ppui": "true"
    }))
  }, cvvDetails, {
    "data-ppui": "true"
  })))));
};
export { CreditCardInput, CreditCardInputUtils, styles as creditCardInputStyles };